import { action } from "@ember/object"
import Service from "@ember/service"
import { tracked } from "@glimmer/tracking"
import type { CardStandard, LessonStandard } from "@shared/models/card-stack"
import type { Course } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"

/** The standards and standard sets the user chose when they closed the standards search modal. */
export interface StandardsSelection {
  standards: Array<CardStandard>
  standardSetIds: Array<string>
}

/**
 * Service to open the standards modal, by calling openStandardsModal.
 */
export default class StandardsModalService extends Service {
  /** Whether the standards search modal is currently shown. */
  @tracked isOpen = false

  /** The standards the modal was opened with, used to seed it. */
  @tracked currentStandards: Array<LessonStandard> = []

  /** The planbook the standards belong to, used to record its recently-used standard sets. */
  @tracked planbook: Planbook | null = null

  /** The course the standards belong to, used to scope the search and record its standard sets. */
  @tracked course: Course | null = null

  /** Resolver for the promise returned by the in-flight {@link openStandardsModal} call. */
  private resolver: ((selection: StandardsSelection) => void) | null = null

  /**
   * Opens the standards search modal seeded with the given standards.
   * Resolves once the user closes it, with the standards and standard sets they chose.
   */
  openStandardsModal(
    currentStandards: Array<LessonStandard>,
    planbook: Planbook,
    course: Course
  ): Promise<StandardsSelection> {
    this.currentStandards = currentStandards
    this.planbook = planbook
    this.course = course
    this.isOpen = true
    return new Promise((resolve) => {
      this.resolver = resolve
    })
  }

  @action
  close(selection: StandardsSelection) {
    this.isOpen = false
    const resolve = this.resolver
    this.resolver = null
    resolve?.(selection)
  }
}

declare module "@ember/service" {
  interface Registry {
    "standards-modal": StandardsModalService
  }
}
